import { createContext, useContext } from "react";
import { useSessionState } from "@/hooks/useSessionState";
import { useAuth } from "@/contexts/AuthContext";
import type { AppRole } from "./roles";

// Re-exports pour compat : les imports existants `@/contexts/RoleContext`
// continuent à trouver les constantes et le type.
export { ROLE_LABELS, ALL_ROLES, NON_ADMIN_ROLES, PERMISSION_ROLES, type AppRole } from "./roles";

interface RoleContextType {
  role: AppRole;
  setRole: (r: AppRole) => void;
}

const RoleContext = createContext<RoleContextType>({
  role: "admin",
  setRole: () => {},
});

export function RoleProvider({ children }: { children: React.ReactNode }) {
  const { isDevSession, profile } = useAuth();
  const [sessionRole, setSessionRole] = useSessionState<AppRole>("app-role", "admin");

  const isAdmin = profile?.role === "admin";

  // - Dev session (picker) : rôle = celui du profile choisi, pas de switch possible.
  // - Vrai admin Supabase : peut visuellement simuler un autre rôle via sessionStorage.
  // - Autres rôles : rôle dérivé du profile DB.
  const role: AppRole = isDevSession
    ? ((profile?.role as AppRole) ?? "collaborateur")
    : isAdmin
      ? sessionRole
      : (profile?.role as AppRole) ?? "collaborateur";

  // Switch autorisé uniquement pour un vrai admin (pas en dev session).
  const setRole = (isAdmin && !isDevSession) ? setSessionRole : () => {};

  return <RoleContext.Provider value={{ role, setRole }}>{children}</RoleContext.Provider>;
}

export function useRole() {
  return useContext(RoleContext);
}
